/**
 * Environment variable utilities
 * Provides safe access to environment variables in both browser (import.meta) and Node.js (process.env) contexts
 */

// Check if we're in a browser environment
const isBrowser =
  typeof window !== "undefined" && typeof window.document !== "undefined";

// Type declaration for process.env to avoid TypeScript errors
declare const process:
  | {
      env: Record<string, string | undefined>;
      NODE_ENV?: string;
      VITEST?: string;
    }
  | undefined;

/**
 * Get an environment variable safely, working in both browser and Node.js contexts
 * @param key The environment variable key (e.g., 'VITE_SUPABASE_URL')
 * @param defaultValue Optional default value if the variable is not found
 * @returns The environment variable value or defaultValue
 */
/**
 * Masque une valeur pour les logs : garde les 6 premiers caractères et
 * remplace le reste par "…". Évite de dumper un token entier dans la console.
 */
function maskEnvValue(key: string, value: string): string {
  const sensitive =
    /key|token|secret|password|anon|service_role|api_key|pat/i.test(key);
  if (!sensitive) return value;
  return value.length > 6
    ? `${value.slice(0, 6)}…(${value.length} chars)`
    : "…";
}

export function getEnv(key: string, defaultValue?: string): string | undefined {
  // En environnement de test Node.js, prioriser process.env (qui contient .env.local)
  // Cela permet aux tests d'utiliser les vraies valeurs depuis .env.local
  const isTestEnv =
    !isBrowser &&
    typeof process !== "undefined" &&
    process?.env &&
    (process.env.NODE_ENV === "test" || process.env.VITEST === "true");

  if (isTestEnv && process.env[key]) {
    return process.env[key];
  }

  // Try import.meta.env first (Vite/browser context)
  if (typeof import.meta !== "undefined" && import.meta.env) {
    const value = import.meta.env[key];
    if (value !== undefined) {
      if (import.meta.env.DEV) {
        console.debug(
          `[getEnv] ${key} = ${maskEnvValue(key, String(value))} (import.meta.env)`,
        );
      }
      return String(value);
    }
  }

  // Only check process.env in non-browser environments (Node.js/SSR)
  if (!isBrowser && typeof process !== "undefined" && process?.env) {
    const value = process.env[key];
    if (value !== undefined) {
      return value;
    }
  }

  return defaultValue;
}

/**
 * Check if we're in development mode
 */
export function isDev(): boolean {
  if (typeof import.meta !== "undefined" && import.meta.env) {
    // Local dev ou preview Vercel
    return (
      import.meta.env.DEV === true ||
      import.meta.env.MODE === "development" ||
      import.meta.env.VITE_VERCEL_ENV === "preview"
    );
  }

  // Only check process.env in non-browser environments (Node.js/SSR)
  if (!isBrowser && typeof process !== "undefined" && process?.env) {
    return process.env.NODE_ENV === "development";
  }

  // Default to production in browser if we can't determine
  return false;
}

/**
 * Get the current mode (development, production, test)
 */
export function getMode(): string {
  if (typeof import.meta !== "undefined" && import.meta.env?.MODE) {
    return String(import.meta.env.MODE);
  }

  // Only check process.env in non-browser environments (Node.js/SSR)
  if (!isBrowser && typeof process !== "undefined" && process?.env?.NODE_ENV) {
    return process.env.NODE_ENV;
  }

  // Default to production in browser if we can't determine
  return "production";
}

/**
 * Validate required environment variables for production
 * @param requiredVars Array of required environment variable keys
 * @returns Object with validation results
 */
export function validateEnvVars(requiredVars: string[]): {
  isValid: boolean;
  missing: string[];
  present: string[];
} {
  const missing: string[] = [];
  const present: string[] = [];

  for (const key of requiredVars) {
    const value = getEnv(key);
    if (!value || value.trim() === "") {
      missing.push(key);
    } else {
      present.push(key);
    }
  }

  return {
    isValid: missing.length === 0,
    missing,
    present,
  };
}

/**
 * Get required environment variables for production
 * @returns Array of required environment variable keys
 */
export function getRequiredProductionVars(): string[] {
  return ["VITE_SUPABASE_URL", "VITE_SUPABASE_ANON_KEY"];
}

/**
 * Validate production environment variables
 * Throws an error if any required variables are missing
 */
export function validateProductionEnv(): void {
  if (!isDev() && getMode() === "production") {
    const requiredVars = getRequiredProductionVars();
    const validation = validateEnvVars(requiredVars);

    if (!validation.isValid) {
      const error = new Error(
        `Variables d'environnement de production manquantes: ${validation.missing.join(", ")}`,
      ) as Error & { code: string; missing: string[] };
      error.code = "MISSING_ENV_VARS";
      error.missing = validation.missing;
      throw error;
    }
  }
}
